import {
  DataGridPremium,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  gridClasses
} from "@mui/x-data-grid-premium";
import Grid from "@mui/material/Unstable_Grid2";
import {
  UserNotifications,
  showToastForBulkResult,
  useOpenSearchUsers
} from "./userUtils";
import { useTranslation } from "react-i18next";
import {
  ProgramRoleSelectFields,
  UserNetworkErrors
} from "../../app/constants";
import { DataGridErrorOverlay } from "../../common-ui/DataGridErrorOverlay";
import { useEffect, useState, useMemo } from "react";
import {
  InstitutionNetworkErrors,
  useAllInstitutions
} from "../Institutions/institutionUtils";
import { useAppSelector } from "../../app/hooks";
import {
  selectInstitutions,
  selectInstitutionsError,
  selectInstitutionsStatus
} from "../Institutions/institutionsSlice";
import { selectCurrentUser, selectUserRoles } from "./usersSlice";
import { Role } from "../Auth/roles";
import { UserTableProps } from "../../app/types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select
} from "@mui/material";
import { ProgramMembership, ProgramRole } from "../../API";
import {
  alphabetizePrograms,
  programLabel,
  programsVisibleTo,
  useAllPrograms,
} from "../Programs/programUtils";
import {
  GroupAdd,
  PersonAdd,
  ScheduleSendRounded
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  setShowToast,
  setToastAutoHideDuration,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { useDispatch } from "react-redux";
import {
  useBulkResendUserInvitations,
  buildResendSummaryMessage
} from "../UserInvitations/userInvitationUtils";
import {
  setModalBoxStyle,
  setModalContent,
  setModalTitle,
  setShowModal
} from "../../common-ui/modalSlice";
import PgyDeltaInput from "./PgyDeltaInput";
import {
  applyPgyBumps,
  canBumpUser,
  getPgyBumpPlan,
  useTrainingStages
} from "../TrainingStages/trainingStagesAPI";
import { updateUser } from "./usersAPI";
import { useBulkEndTrayHydration } from "./useBulkEndTrayHydration";
import {
  addToTray,
  selectTrayCount,
  BulkEndTrayEntry
} from "./bulkEndTraySlice";
import { BulkEndReviewDialog } from "./BulkEndReviewDialog";
import PlaylistAddCheckRounded from "@mui/icons-material/PlaylistAddCheckRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import { ProgramUser } from "../../custom-types";
import { uniqBy } from "lodash";

type SysAdminsUsersTableProps = UserTableProps;

export const ProgramRoleSelectFieldsNew = [
  { label: "Clinician Trainee", value: "Trainee" },
  { label: "Clinician Non Trainee", value: "Attending" },
  { label: "Program Administrator", value: ProgramRole.ProgramAdmin },
  {
    label: "Institutional Administrator",
    value: ProgramRole.Admin
  }
];

export const SysAdminsUsersTable = ({
  disableVirtualization = false,
  columns,
  selectedProgramId,
  setSelectedProgramId,
  handleRowClick
}: SysAdminsUsersTableProps) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentUsersRoles = useAppSelector(selectUserRoles);
  const isSysAdmin = !!currentUsersRoles?.includes(Role.SystemAdmin);

  const { programs } = useAllPrograms();

  const [selectedRole, setSelectedRole] = useState<ProgramRole | string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);

  const [pgyBumpValue, setPgyBumpValue] = useState<number | undefined>(1);
  const [pgyModalInvocations, setPgyModalInvocations] = useState(0);
  const { trainingStages } = useTrainingStages();

  const [refetchUsers, setRefetchUsers] = useState<number>(0);

  // SE-1723: keep the bulk-end tray owned by the logged-in Sys Admin.
  useBulkEndTrayHydration();
  const trayCount = useAppSelector(selectTrayCount);
  const [bulkEndDialogOpen, setBulkEndDialogOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    status: resendInvitationsStatus,
    successCount,
    currentStep,
    summary: resendSummary,
    bulkResendUserInvitations
  } = useBulkResendUserInvitations();

  // Always use server path; remove programId client fetch

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "firstName", sort: "asc" }
  ]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: []
  });

  // Status is forwarded as a top-level AND clause; no need to inject into filterModel

  // Use Redux institutions (pre-loaded for SysAdmins in auth flow)
  // For SysAdmins, institutions are already loaded in auth flow, so just use Redux
  const institutions = useAppSelector(selectInstitutions);
  const institutionsStatus = useAppSelector(selectInstitutionsStatus);
  const institutionsError = useAppSelector(selectInstitutionsError);
  
  // Memoize empty arrays to prevent creating new references on every render
  const memoizedInstitutions = useMemo(() => institutions || [], [institutions]);
  const memoizedPrograms = useMemo(() => programs || [], [programs]);
  
  const { users, status, error, total, nextPage, prevPage } =
    useOpenSearchUsers(
      paginationModel.pageSize,
      sortModel,
      filterModel,
      memoizedInstitutions,
      memoizedPrograms,
      refetchUsers,
      selectedProgramId || undefined,
      selectedRole || undefined,
      selectedStatus || undefined,
      false
    );

  const enrichUser = (user: ProgramUser): ProgramUser => {
    const programMemberships = user.programMemberships?.items || [];
    const activeMemberships = programMemberships.filter((m) => !m?.endDate);

    const rawRoles = activeMemberships
      .map((membership) => membership?.programRole)
      .filter((r): r is ProgramRole => !!r); // Type narrowing

    const roleSet = new Set<string>();
    let hasClinician = false;
    rawRoles.forEach((role) => {
      if (role === "Trainee" || role === "Attending") {
        hasClinician = true;
      } else {
        roleSet.add(role);
      }
    });
    if (hasClinician) roleSet.add("Clinician");

    const trainingPrograms = programs
      .filter((p) =>
        activeMemberships.some(
          (m) => m?.programId === p.id && m.programRole === "Trainee"
        )
      )
      .map((p) => p.name)
      .join(", ");

    const institutionName = programs
      .filter((p) => activeMemberships.some((m) => m?.programId === p.id))
      .map((p) => p.institution?.name)
      .join(", ");

    const programName =
      programs.find((p) => activeMemberships.some((m) => m?.programId === p.id))
        ?.name ?? "N/A";

    // Job title calculation rules:
    // 1) If a jobTitle exists from backend, use it.
    // 2) Else, use the raw backend role if present (Attending, Trainee, Program Admin), otherwise blank.
    const hasBackendTrainee = activeMemberships.some(
      (m) => m?.programRole === "Trainee"
    );
    const hasBackendAttending = activeMemberships.some(
      (m) => m?.programRole === "Attending"
    );
    const hasBackendProgramAdmin = activeMemberships.some(
      (m) => m?.programRole === ProgramRole.ProgramAdmin
    );
    const backendRoleForJobTitle =
      (hasBackendTrainee && "Trainee") ||
      (hasBackendAttending && "Attending") ||
      (hasBackendProgramAdmin && "Program Admin") ||
      undefined;

    const computedJobTitle = (
      user.jobTitle && String(user.jobTitle).trim().length > 0
        ? String(user.jobTitle).trim()
        : backendRoleForJobTitle
    ) as string | undefined;

    return {
      ...(user as any),
      role: Array.from(roleSet).join(", "),
      rawRoles: programMemberships
        .filter(
          (m): m is ProgramMembership & { programRole: ProgramRole } =>
            !!m?.programRole && !m.endDate
        )
        .map((m) => m.programRole),
      trainingProgram: trainingPrograms,
      institutionName,
      program: programName,
      jobTitle: computedJobTitle,
      status: user.eulaAcceptedDate ? "Active" : "Unregistered"
    };
  };

  const queriedUsers = (() => {
    const enriched = ((users as ProgramUser[]) || [])
      .map(enrichUser)
      .filter(Boolean);
    return enriched;
  })();

  const filteredUsers = queriedUsers;

  // This grid is server-paginated/filtered, so a row selected under one view
  // isn't necessarily loaded under the next. Clear the selection whenever the
  // view changes so the selection always matches what's on screen — otherwise
  // a bulk action could hit hidden, stale selections (the resend-storm risk).
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (!selectedProgramId) {
      if (model.page > paginationModel.page) {
        nextPage();
      }
      if (model.page < paginationModel.page) {
        prevPage();
      }
    }
    setPaginationModel(model);
    setSelectedUserIds([]);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    setSelectedUserIds([]);
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    setSelectedUserIds([]);
  };

  // SE-1723: add the currently-selected users to the persistent end-list tray.
  // This explicit action is the deliberate gate (the grid selection itself
  // clears on view change), and the tray survives search/sort/filter/paging.
  const handleAddSelectedToEndList = () => {
    const toAdd: BulkEndTrayEntry[] = selectedUserIds
      .map((id) => filteredUsers.find((u) => u.id === id))
      .filter(Boolean)
      .map((u) => {
        // Admit EVERY selected user — do NOT gate on membership data here.
        // The users-list table carries ONLY active memberships (the backend
        // filters end_date IS NULL), so a user whose memberships are all
        // ENDED shows up with an empty snapshot. That's exactly the user the
        // "change ended dates" mode targets, so gating on the snapshot would
        // make that mode unusable. The review dialog re-fetches BOTH active +
        // ended via fetchUser on open, so the snapshot is only a hint.
        const active = ((u as ProgramUser).programMemberships?.items || [])
          .filter((m): m is ProgramMembership => !!m && !m.endDate);
        return {
          userId: (u as ProgramUser).id,
          firstName: (u as ProgramUser).firstName,
          lastName: (u as ProgramUser).lastName,
          email: (u as ProgramUser).email,
          memberships: active.map((m) => ({
            id: m.id,
            programId: m.programId ?? null,
            programName: programs.find((p) => p.id === m.programId)?.name ?? null,
            programRole: m.programRole ?? null
          }))
        } as BulkEndTrayEntry;
      });

    toAdd.forEach((e) => dispatch(addToTray(e)));
    setSelectedUserIds([]);
    dispatch(setToastStyle("info"));
    dispatch(
      setToastMessage(
        `Added ${toAdd.length} ${
          toAdd.length === 1 ? "user" : "users"
        } to the list`
      )
    );
    dispatch(setToastAutoHideDuration(5000));
    dispatch(setShowToast(true));
  };

  const anyApiIsLoading = [
    institutionsStatus,
    status,
    resendInvitationsStatus
  ].includes("loading");

  useEffect(() => {
    if (resendInvitationsStatus === "idle") return;

    if (resendInvitationsStatus === "loading") {
      dispatch(setToastStyle("info"));
      dispatch(setToastMessage(t("Resending invitations...")));
      dispatch(setToastAutoHideDuration(null));
      dispatch(setShowToast(true));
    }

    if (resendInvitationsStatus === "succeeded") {
      const { message, style } = resendSummary
        ? buildResendSummaryMessage(resendSummary)
        : {
            message: `Invitation has been re-sent successfully to ${successCount} ${
              successCount === 1 ? "person" : "people"
            }.`,
            style: "success" as const
          };
      dispatch(setToastStyle(style));
      dispatch(setToastMessage(t(message)));
      // Persist until dismissed — the result summary is easy to miss otherwise.
      dispatch(setToastAutoHideDuration(null));
      // Clear the selection so a follow-up action doesn't re-send to the same
      // (growing) list by accident. Does not affect the "+1 Year" flow.
      setSelectedUserIds([]);
    }

    if (resendInvitationsStatus === "failed") {
      dispatch(setToastStyle("error"));
      dispatch(
        setToastMessage(
          t(
            "An error ocurred re-sending the invitations. Please contact your administrator for further assistance."
          )
        )
      );
      dispatch(setToastAutoHideDuration(null));
    }
    dispatch(setShowToast(true));
  }, [resendInvitationsStatus, successCount, currentStep, resendSummary]);

  // Removed programId client-fetch effect

  // When switching from program-selected (client mode) back to all programs (server mode),
  // reset the grid to the first page so the server fetch starts at page 1.
  useEffect(() => {
    if (!selectedProgramId) {
      setPaginationModel((prev) => ({
        page: 0,
        pageSize: prev.pageSize
      }));
    }
  }, [selectedProgramId]);

  useEffect(() => {
    const selectedUsers = selectedUserIds
      .map((userId) => filteredUsers.find((user) => user.id === userId))
      .filter(Boolean)
      .filter((user: any) =>
        canBumpUser(user, trainingStages)
      ) as typeof filteredUsers;

    dispatch(
      setModalContent(
        <Box
          sx={{ display: "flex", flexDirection: "column" }}
          alignItems="center"
          gap={3}
          padding={3}
        >
          <Box>
            You are updating the Clinical Year by{" "}
            <PgyDeltaInput
              setDeltaValue={setPgyBumpValue}
              currentDeltaValue={pgyBumpValue}
            />{" "}
            for {selectedUsers.length} people
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!pgyBumpValue}
            onClick={() => {
              if (!pgyBumpValue) return;

              const plans = selectedUsers
                .map((user: any) =>
                  getPgyBumpPlan(user, trainingStages, pgyBumpValue)
                )
                .filter((p): p is NonNullable<typeof p> => p !== null);

              if (plans.length === 0) {
                dispatch(setToastStyle("error"));
                dispatch(
                  setToastMessage(
                    t(
                      "Clinical Year cannot be updated for any selected users."
                    )
                  )
                );
                dispatch(setShowToast(true));
                return;
              }

              applyPgyBumps(plans, (input) =>
                updateUser(input as any)
              )
                .then((result) => {
                  const successes =
                    result.yearInStageUpdated + result.pgyUpdated;
                  const expected =
                    plans.filter((p) => p.newYearInStage != null).length +
                    plans.filter((p) => p.newPgy != null).length;
                  const summary = {
                    fulfilled: successes,
                    rejected: Math.max(expected - successes, 0)
                  };
                  const skipNotes: string[] = [];
                  if (result.yearInStageSkipped.length > 0) {
                    skipNotes.push(
                      `${result.yearInStageSkipped.length} skipped: out of range`
                    );
                  }
                  if (result.pgyFailed.length > 0) {
                    skipNotes.push(
                      `${result.pgyFailed.length} failed`
                    );
                  }
                  const skippedSuffix =
                    skipNotes.length > 0 ? ` (${skipNotes.join("; ")})` : "";
                  showToastForBulkResult(
                    dispatch,
                    summary,
                    // Compare against field count (not user count): Fellowship
                    // updates 2 fields/user, so a full success must equal
                    // `expected` fields to be styled green rather than yellow.
                    expected,
                    t(
                      `You have successfully updated Clinical Year for ${
                        plans.length
                      } ${plans.length > 1 ? "users" : "user"}`
                    ),
                    t(
                      `Clinical Year updated for ${result.yearInStageUpdated + result.pgyUpdated} of ${expected} field(s) across ${plans.length} users${skippedSuffix}`
                    ),
                    t(
                      "An error occurred updating Clinical Year, please try again later"
                    )
                  );
                  setTimeout(() => setRefetchUsers(refetchUsers + 1), 500);
                })
                .catch((err) => {
                  console.error(
                    "Error updating Clinical Year:",
                    err
                  );
                  dispatch(setToastStyle("error"));
                  dispatch(
                    setToastMessage(
                      t(
                        "An error occurred updating Clinical Year, please try again later"
                      )
                    )
                  );
                  dispatch(setShowToast(true));
                });
              dispatch(setShowModal(false));
            }}
          >
            CONFIRM
          </Button>
        </Box>
      )
    );
    dispatch(setModalBoxStyle({ width: 444, minWidth: 444 }));
    if (pgyModalInvocations > 0) dispatch(setShowModal(true));
  }, [pgyModalInvocations, pgyBumpValue, trainingStages]);

  return (
    <Grid
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      spacing={2}
      gap={2}
    >
      {(status === "failed" || error) && (
        <DataGridErrorOverlay
          errorMessage={UserNetworkErrors.FetchAllUsersError}
        />
      )}
      {(institutionsError === "failed" || institutionsError) && (
        <DataGridErrorOverlay
          errorMessage={InstitutionNetworkErrors.FetchAllInstitutionsError}
        />
      )}
      <Grid xs={12} pt={2}>
        <Grid xs={12} container spacing={2}>
          <Grid>
            <FormControl>
              <InputLabel shrink id="program-label">
                {t("Program")}
              </InputLabel>
              <Select
                labelId="program-label"
                label={t("Programs")}
                id="programSelection"
                style={{ width: 300 }}
                size="small"
                displayEmpty
                value={selectedProgramId || ""}
                input={<OutlinedInput notched label={t("Programs")} />}
                onChange={(event) => {
                  if (setSelectedProgramId) {
                    setSelectedProgramId(event.target.value);
                  }
                  setSelectedUserIds([]);
                }}
              >
                <MenuItem value={""} key={"no-program"}>
                  All
                </MenuItem>
                {alphabetizePrograms(
                  programsVisibleTo(programs ?? [], isSysAdmin),
                  true
                )?.map((program) => (
                  <MenuItem value={program.id} key={program.id}>
                    {programLabel(program)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl>
              <InputLabel shrink id="role-label">
                {t("Role")}
              </InputLabel>
              <Select
                disabled={!selectedProgramId}
                labelId="role-label"
                id="roleSelection"
                label={t("Role")}
                style={{ width: 300 }}
                size="small"
                displayEmpty
                value={selectedRole || ""}
                input={<OutlinedInput notched label={t("Role")} />}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === "") {
                    setSelectedRole(value);
                  } else {
                    setSelectedRole(value as ProgramRole);
                  }
                  setSelectedUserIds([]);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {ProgramRoleSelectFieldsNew?.map((roleSelectField) => (
                  <MenuItem
                    value={roleSelectField.value}
                    key={roleSelectField.value}
                  >
                    {roleSelectField.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl>
              <InputLabel shrink id="status-label">
                {t("Status")}
              </InputLabel>
              <Select
                labelId="status-label"
                id="statusSelection"
                label={t("Status")}
                style={{ width: 300 }}
                size="small"
                displayEmpty
                value={selectedStatus || ""}
                input={<OutlinedInput notched label={t("Status")} />}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedStatus(value);
                  setSelectedUserIds([]);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {[
                  { value: "Active", label: "Active" },
                  { value: "Unregistered", label: "Unregistered" }
                ]?.map((roleSelectField) => (
                  <MenuItem
                    value={roleSelectField.value}
                    key={roleSelectField.value}
                  >
                    {roleSelectField.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      <Grid xs={12} container spacing={2}>
        <Grid>
          <Button
            disabled={anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<PersonAdd />}
            onClick={() => navigate("/invitations/new")}
          >
            {t("NEW USER")}
          </Button>
        </Grid>

        <Grid>
          <Button
            disabled={anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<GroupAdd />}
            onClick={() => navigate("/invitations/bulkupload")}
          >
            {t("BULK NEW USERS")}
          </Button>
        </Grid>

        <Grid>
          <LoadingButton
            disabled={selectedUserIds.length === 0 || anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<ScheduleSendRounded />}
            loading={resendInvitationsStatus === "loading"}
            loadingPosition="start"
            onClick={() => {
              setResendConfirmOpen(true);
            }}
          >
            {t("RESEND INVITATION")}
          </LoadingButton>
        </Grid>

        <Grid>
          <Button
            disabled={selectedUserIds.length === 0 || anyApiIsLoading}
            variant="contained"
            size="small"
            onClick={() => {
              const selectedUsers = selectedUserIds
                .map((userId) =>
                  filteredUsers.find((user) => user.id === userId)
                )
                .filter(Boolean)
                .filter((user: any) =>
                  canBumpUser(user, trainingStages)
                ) as typeof filteredUsers;

              if (!selectedUsers?.length) {
                dispatch(
                  setToastMessage(
                    t(
                      "None of the selected users have a Clinical Year value set. Set one via Edit User before bumping."
                    )
                  )
                );
                dispatch(setToastStyle("error"));
                dispatch(setShowToast(true));
                return;
              }

              setPgyModalInvocations(pgyModalInvocations + 1);
            }}
          >
            {t("+1 Year")}
          </Button>
        </Grid>

        <Grid>
          <Button
            disabled={selectedUserIds.length === 0 || anyApiIsLoading}
            variant="contained"
            size="small"
            startIcon={<PlaylistAddCheckRounded />}
            onClick={handleAddSelectedToEndList}
          >
            {t("ADD TO END LIST")}
          </Button>
        </Grid>

        <Grid>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EventBusyRounded />}
            disabled={trayCount === 0}
            onClick={() => setBulkEndDialogOpen(true)}
          >
            {t(`END LIST (${trayCount})`)}
          </Button>
        </Grid>

        {/* Create a modal to show that report generation has been initiated w/ email message */}
      </Grid>

      <Grid xs={12} sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <DataGridPremium
          disableVirtualization={disableVirtualization}
          checkboxSelection
          aria-label="users-table-sysadmins"
          rows={filteredUsers || []}
          columns={columns}
          loading={anyApiIsLoading}
          rowCount={total}
          localeText={{
            noRowsLabel: t(UserNotifications.NoUsers)
          }}
          rowSelectionModel={selectedUserIds}
          onRowSelectionModelChange={(selection) => {
            setSelectedUserIds(selection as string[]);
          }}
          paginationMode={"server"}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortingMode={"server"}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterMode={"server"}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onRowClick={handleRowClick}
          getRowHeight={() => "auto"}
          sx={{
            flex: 1,
            minHeight: 0,
            [`& .${gridClasses.cell}`]: {
              py: 1,
              whiteSpace: "pre-wrap"
            }
          }}
        />
      </Grid>

      <Dialog
        open={resendConfirmOpen}
        onClose={() => setResendConfirmOpen(false)}
        aria-labelledby="resend-confirm-title-sysadmin"
      >
        <DialogTitle id="resend-confirm-title-sysadmin">
          {t("Resend invitations?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              `This will email an invitation to ${selectedUserIds.length} ${
                selectedUserIds.length === 1 ? "user" : "users"
              }. They will each receive a registration email now. Only proceed if you intend to notify them.`
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendConfirmOpen(false)}>
            {t("Cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            startIcon={<ScheduleSendRounded />}
            loading={resendInvitationsStatus === "loading"}
            loadingPosition="start"
            onClick={() => {
              // Snapshot the IDs, then clear the selection immediately — the grid
              // is controlled by selectedUserIds, so this empties both the
              // checkboxes and the underlying state at once. No stale/hidden
              // selection can survive to be re-sent by a later action.
              const idsToSend = [...selectedUserIds];
              setResendConfirmOpen(false);
              setSelectedUserIds([]);
              bulkResendUserInvitations(idsToSend);
            }}
          >
            {t("Send")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <BulkEndReviewDialog
        open={bulkEndDialogOpen}
        onClose={() => setBulkEndDialogOpen(false)}
        // Refetch AFTER a short delay — the just-written end dates need a beat
        // to propagate before the users-list read reflects them (same 500ms
        // pattern as the bulk Clinical Year update above). Functional update so
        // it can't collide with a stale refetchUsers closure.
        onDone={() => setTimeout(() => setRefetchUsers((n) => n + 1), 500)}
      />
    </Grid>
  );
};
